import { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Button } from 'antd'
import { Icon, Table } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { AIChatbotFAQFragment } from 'types/graphql'

import CustomAnswerModal from 'src/components/AIAssistant/CustomAnswerModal'
import { useSearchAIChatbotFAQsQuery } from 'src/fetch/aiChatbotFAQ'
import { useActiveOrgId } from 'src/hooks/use-active-org-id'
import useDisclosure from 'src/hooks/use-disclosure'
import { AIChatbotFAQCountColumn } from 'src/pages/AIAssistantTrainPage/columns'
import CustomAnswersTableButtons from 'src/pages/AIAssistantTrainPage/CustomAnswersTableButtons'
import ParsedHtmlMessage from 'src/pages/AIChat/AIResponseMessage/ParsedHtmlMessage'

const CustomAnswersTable = () => {
  const organizationId = useActiveOrgId()
  const getRowId = useCallback((aiChatbotFAQ: AIChatbotFAQFragment) => `${aiChatbotFAQ.id}`, [])
  const { debouncedSearchQuery, ...tableProps } = useTableState<AIChatbotFAQFragment>()
  const { faqs, count, isFetching, refetch } = useSearchAIChatbotFAQsQuery({
    organizationId,
    pagination: {
      page: tableProps.pagination.pageIndex + 1,
      pageSize: tableProps.pagination.pageSize,
      query: debouncedSearchQuery,
    },
    type: 'Answered',
  })

  const createModalState = useDisclosure()

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AIChatbotFAQFragment>()
    return [
      AIChatbotFAQCountColumn(columnHelper),
      columnHelper.accessor((aiChatbotFAQ) => aiChatbotFAQ.question, {
        id: 'question',
        header: 'Question',
        cell: (info) => <ParsedHtmlMessage className="!w-[300px]" html={info.getValue() ?? ''} />,
        enableSorting: false,
      }),
      columnHelper.accessor((aiChatbotFAQ) => aiChatbotFAQ.answer, {
        id: 'answer',
        header: 'Answer',
        cell: (info) => <ParsedHtmlMessage className="!w-[300px]" html={info.getValue() ?? ''} />,
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row: { original: aiChatbotFAQ } }) => (
          <CustomAnswersTableButtons aiChatbotFAQ={aiChatbotFAQ} refetch={refetch} />
        ),
        meta: {
          width: '100px',
          minWidth: '25px',
        },
      }),
    ]
  }, [refetch])

  return (
    <>
      <Table
        title="Custom Answers"
        actions={
          <Button type="primary" icon={<Icon.FaPlus />} onClick={createModalState.open}>
            Create custom answer
          </Button>
        }
        columns={columns}
        data={faqs}
        enablePagination
        enableSearching
        getRowId={getRowId}
        isLoading={isFetching}
        totalCount={count}
        showCount
        emptyStateMessage="No answers found. As users interact with the Community Assistant, they will be added here."
        {...tableProps}
      />
      {createModalState.isOpen && (
        <CustomAnswerModal modalType="create" onSubmit={refetch} onClose={createModalState.close} />
      )}
    </>
  )
}

export default React.memo(CustomAnswersTable)
